/**
 * Weekly Agenda View Component
 *
 * Top-level container for the weekly agenda.
 * Combines header + swipeable grid using reanimated + gesture-handler.
 *
 * Follows single responsibility: orchestrates weekly view layout and swipe gestures.
 */

import React, { useCallback, useRef } from 'react';
import { Dimensions, StyleSheet, View } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { WeeklyAgendaHeader } from './WeeklyAgendaHeader';
import { WeeklyAgendaGrid, type WeeklyAgendaGridHandle } from './WeeklyAgendaGrid';
import { OneTimeCallout } from '@/components/shared/OneTimeCallout';
import { useOneTimeCallout } from '@/hooks/use-one-time-callout';
import type { FamilyMemberResource } from '@/types/family-resource';
import type { WeeklyAgendaWeekData } from '@/types/weekly-agenda';
import type { CalendarEvent } from '@/types/events';

const SCREEN_WIDTH = Dimensions.get('window').width;
const SWIPE_THRESHOLD = SCREEN_WIDTH * 0.12;
const VELOCITY_THRESHOLD = 500;
const ANIMATION_DURATION = 250;

type WeeklyAgendaViewProps = {
  weekData: WeeklyAgendaWeekData;
  resources: FamilyMemberResource[];
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onTodayPress: () => void;
  onMenuPress: () => void;
  onDayPress: (dateKey: string) => void;
  onEventPress?: (event: CalendarEvent) => void;
  onAvatarPress?: (resource: FamilyMemberResource) => void;
  onCreateEvent?: (dateKey: string, memberId: string) => void;
  onAddEventPress?: () => void;
  onHeaderTextPress?: () => void;
  showCalendarSelector: boolean;
  initialScrollDateKey?: string;
  scrollToTodayTrigger?: number;
  selectedDateKey?: string;
};

export type WeeklyAgendaViewHandle = {
  scrollToDay: (dateKey: string) => void;
};

export const WeeklyAgendaView = React.forwardRef<WeeklyAgendaViewHandle, WeeklyAgendaViewProps>(
  function WeeklyAgendaView(
    {
      weekData,
      resources,
      onPrevWeek,
      onNextWeek,
      onTodayPress,
      onMenuPress,
      onDayPress,
      onEventPress,
      onAvatarPress,
      onCreateEvent,
      onAddEventPress,
      onHeaderTextPress,
      showCalendarSelector,
      initialScrollDateKey,
      scrollToTodayTrigger,
      selectedDateKey,
    },
    ref
  ) {
    const translateX = useSharedValue(0);
    const gridRef = useRef<WeeklyAgendaGridHandle>(null);
    const { isCalloutVisible, dismissCallout } = useOneTimeCallout('fimfam-ai:callout:weekly-nav');

    // Expose scrollToDay to parent via ref
    React.useImperativeHandle(ref, () => ({
      scrollToDay: (dateKey: string) => gridRef.current?.scrollToDay(dateKey),
    }), []);

    const handleHeaderTextPress = useCallback(() => {
      dismissCallout();
      onHeaderTextPress?.();
    }, [dismissCallout, onHeaderTextPress]);

    const handleDayPress = useCallback((dateKey: string) => {
      dismissCallout();
      onDayPress(dateKey);
    }, [dismissCallout, onDayPress]);

    const navigatePrev = useCallback(() => {
      onPrevWeek();
    }, [onPrevWeek]);

    const navigateNext = useCallback(() => {
      onNextWeek();
    }, [onNextWeek]);

    const resetPosition = useCallback(() => {
      translateX.value = withTiming(0, { duration: ANIMATION_DURATION });
    }, [translateX]);

    const animateAndNavigate = useCallback((direction: 'left' | 'right') => {
      const targetX = direction === 'left' ? -SCREEN_WIDTH : SCREEN_WIDTH;
      translateX.value = withTiming(targetX, { duration: ANIMATION_DURATION }, () => {
        if (direction === 'left') {
          runOnJS(navigateNext)();
        } else {
          runOnJS(navigatePrev)();
        }
        translateX.value = 0;
      });
    }, [translateX, navigateNext, navigatePrev]);

    const panGesture = Gesture.Pan()
      .activeOffsetX([-10, 10])
      .failOffsetY([-10, 10])
      .onUpdate((event) => {
        translateX.value = event.translationX;
      })
      .onEnd((event) => {
        const passedThreshold = Math.abs(event.translationX) > SWIPE_THRESHOLD;
        const fastFlick = Math.abs(event.velocityX) > VELOCITY_THRESHOLD;

        if ((passedThreshold || fastFlick) && event.translationX < 0) {
          runOnJS(animateAndNavigate)('left');
        } else if ((passedThreshold || fastFlick) && event.translationX > 0) {
          runOnJS(animateAndNavigate)('right');
        } else {
          runOnJS(resetPosition)();
        }
      });

    return (
      <View style={styles.container}>
        <WeeklyAgendaHeader
          headerLabel={weekData.headerLabel}
          onPrevWeek={onPrevWeek}
          onNextWeek={onNextWeek}
          onTodayPress={onTodayPress}
          onMenuPress={onMenuPress}
          onAddEventPress={onAddEventPress}
          onHeaderTextPress={onHeaderTextPress ? handleHeaderTextPress : undefined}
          showCalendarSelector={showCalendarSelector}
          bareContainer
        />
        <View style={styles.calloutAnchor} pointerEvents="box-none">
          <OneTimeCallout
            visible={isCalloutVisible}
            text="Tap the date above for month view. Tap a day button on the left to open day view."
            onDismiss={dismissCallout}
            pointer="up"
          />
        </View>
        <GestureDetector gesture={panGesture}>
          <Animated.View style={styles.gridContainer}>
            <WeeklyAgendaGrid
              ref={gridRef}
              weekData={weekData}
              resources={resources}
              onDayPress={handleDayPress}
              onEventPress={onEventPress}
              onAvatarPress={onAvatarPress}
              onCreateEvent={onCreateEvent}
              translateX={translateX}
              initialScrollDateKey={initialScrollDateKey}
              scrollToTodayTrigger={scrollToTodayTrigger}
              selectedDateKey={selectedDateKey}
            />
          </Animated.View>
        </GestureDetector>
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  gridContainer: {
    flex: 1,
  },
  calloutAnchor: {
    position: 'relative',
    zIndex: 101,
  },
});
